import React from 'react';
import I18n from 'i18n-js';
import { RCButton, RCIcon, RCText, useRCPopover } from '@reverbdotcom/cadence/components';
import {
  ArrowLeftIcon,
  BankIcon,
  WarningCircleIcon,
} from '@reverbdotcom/cadence/icons/react';
import { SvgMapPin, SvgUsers } from './action_icons';
import { NotificationLink } from './notification_link';
import { NotificationsCenterQuery } from '@reverbdotcom/commons/src/gql/graphql';
import { clickedNotificationEvent } from './notification_event_props';
import { useMarkActionRead } from './notifications_read_state';

type NotificationAction = NotificationsCenterQuery['notificationActions']['actions'][number];

const ACTION_ICONS: Record<string, JSX.Element> = {
  bank: <BankIcon />,
  'map-pin': <SvgMapPin />,
  users: <SvgUsers />,
  'warning-circle': <WarningCircleIcon />,
};

function actionIcon(iconName: string): JSX.Element {
  return ACTION_ICONS[iconName] ?? <WarningCircleIcon />;
}

interface IProps {
  actions: NotificationAction[];
  onBack: () => void;
  // The popover focuses this once the rollup has slid in, so the screen reader lands on
  // the new pane instead of on the button that just went offscreen with it.
  backRef?: React.Ref<HTMLButtonElement>;
}

export default function ActionsRollupView({ actions, onBack, backRef }: IProps) {
  const markActionRead = useMarkActionRead();

  return (
    <div className="notifications-center__rollup">
      <div className="notifications-center__rollup__header">
        {/* Without hideTooltip the button's own tooltip and this panel share the
            header's FloatingDelayGroup, and hovering the arrow closes the panel. */}
        <RCButton
          ref={backRef}
          variant="transparent"
          size="small"
          iconOnly={<ArrowLeftIcon />}
          iconLabel={I18n.t('discovery.notificationsCenter.actions.back')}
          hideTooltip
          onClick={onBack}
        />
      </div>

      <ul className="notifications-center__rollup__list">
        {actions.map(action => (
          <li key={action.key}>
            <ActionCard action={action} onMarkRead={markActionRead} />
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ActionCard({ action, onMarkRead }: {
  action: NotificationAction;
  onMarkRead: (action: NotificationAction) => void;
}) {
  // The panel is an RCPopover on desktop, which a click on its own content won't
  // dismiss. A no-op inside the mobile drawer, which the navigation closes anyway.
  const { close } = useRCPopover();

  return (
    <NotificationLink
      to={action.href}
      className="notifications-center__action-card"
      clickEvent={clickedNotificationEvent(action.eventTracking)}
      onClick={() => { onMarkRead(action); close(); }}
    >
      <span className="notifications-center__action-card__icon">
        <RCIcon icon={actionIcon(action.iconName)} size="7" />
      </span>
      <span className="notifications-center__action-card__text">
        <RCText.Utility size="350" weight="semibold" htmlTag="span">
          {action.title}
        </RCText.Utility>
        <RCText.Default size="350" htmlTag="span">
          {action.description}
        </RCText.Default>
      </span>
    </NotificationLink>
  );
}
